import React from 'react';
import { motion } from 'framer-motion';

const SolutionSection = () => {
  const features = [
    "✅ Unlimited Lead Magnets",
    "✅ Unlimited WhatsApp Numbers", 
    "✅ Unlimited Sending & Automations",
    "✅ Unlimited Staffs / Users",
    "✅ Unlimited Groups",
    "✅ Manage Leads + Nurture Customers Effortlessly"
  ];

  return (
    <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
            💡 The Solution: AWA AI – WhatsApp CRM & Automation Tool
          </h2>
          <p className="text-xl sm:text-2xl text-green-300 font-semibold">
            Run Sales on Autopilot – Leads, Nurture, Payments, Everything in One.
          </p>
        </motion.div>

        {/* AWA AI Dashboard Images */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <img 
              src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759114501614-Whatsapp%20sender%20.webp"
              alt="WhatsApp Sender Dashboard"
              className="rounded-2xl shadow-2xl w-full h-64 sm:h-80 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-blue-500 bg-opacity-10 rounded-2xl"></div>
            <div className="absolute bottom-4 left-4 right-4">
              <div className="bg-black bg-opacity-70 rounded-lg p-3">
                <h3 className="text-white text-lg font-bold mb-2">WhatsApp Sender</h3>
                <p className="text-gray-200 text-sm">Efficiency in Messaging: WhatsApp Sender automates the delivery of messages to your contacts, enhancing the efficiency of your communication strategies by eliminating the need for manual texting.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <img 
              src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759114507164-Whatsapp%20Automation%20workflow.webp"
              alt="Automation Workflow Dashboard"
              className="rounded-2xl shadow-2xl w-full h-64 sm:h-80 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 bg-opacity-10 rounded-2xl"></div>
            <div className="absolute bottom-4 left-4 right-4">
              <div className="bg-black bg-opacity-70 rounded-lg p-3">
                <h3 className="text-white text-lg font-bold mb-2">Automation Workflow</h3>
                <p className="text-gray-200 text-sm">Enhanced Business Efficiency: Automation Workflow streamlines your operations by automating repetitive tasks, allowing your team to focus on more strategic initiatives.</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Abdul with Robot Image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="relative max-w-4xl mx-auto">
            <img 
              src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759114057014-abdul%20with%20robo.webp"
              alt="Abdul with AI Robot - AWA AI Solution"
              className="rounded-2xl shadow-2xl w-full h-64 sm:h-80 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-blue-500 bg-opacity-20 rounded-2xl"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white bg-black bg-opacity-50 p-6 rounded-xl">
                <h3 className="text-2xl sm:text-3xl font-bold mb-2">AWA AI Solution</h3>
                <p className="text-lg sm:text-xl">Complete WhatsApp Automation & CRM</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Detailed Feature Descriptions */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white bg-opacity-10 backdrop-blur-lg rounded-xl p-6 border border-white border-opacity-20"
          >
            <h3 className="text-2xl font-bold text-green-400 mb-4">🚀 WhatsApp Sender Benefits</h3>
            <ul className="space-y-3 text-gray-200">
              <li>• <strong>Efficiency in Messaging:</strong> Automates message delivery to contacts</li>
              <li>• <strong>Streamlined Operations:</strong> Focus on strategic tasks instead of routine communications</li>
              <li>• <strong>Enhanced Communication:</strong> Eliminates manual texting requirements</li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-white bg-opacity-10 backdrop-blur-lg rounded-xl p-6 border border-white border-opacity-20"
          >
            <h3 className="text-2xl font-bold text-purple-400 mb-4">⚡ Automation Workflow Benefits</h3>
            <ul className="space-y-3 text-gray-200">
              <li>• <strong>Enhanced Business Efficiency:</strong> Streamlines operations by automating repetitive tasks</li>
              <li>• <strong>Reduced Manual Effort:</strong> Consolidates Email, WhatsApp, and SMS interactions</li>
              <li>• <strong>Unified Platform:</strong> Consistent messaging across all channels</li>
            </ul>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white bg-opacity-10 backdrop-blur-lg rounded-xl p-6 border border-white border-opacity-20 hover:bg-opacity-20 transition-all duration-300"
            >
              <p className="text-white text-lg font-semibold">{feature}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-xl p-6 text-center"
        >
          <p className="text-black text-xl sm:text-2xl font-bold">
            🎁 Free 1-to-1 Expert Onboarding Call → Setup Same Day!
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default SolutionSection;